import React, { useRef } from 'react';
import { cn } from '@/lib/utils';

export interface ChoiceOption {
  value: string;
  label: string;
  icon?: React.ElementType;
}

interface ChoiceGroupProps {
  id: string;
  label: string;
  options: ChoiceOption[];
  value: string | null;
  onChange: (value: string) => void;
  disabled?: boolean;
  invalid?: boolean;
  /** id des Fehlertexts, wenn die Gruppe ungültig ist. */
  errorId?: string;
  variant?: 'chip' | 'card';
}

const NEXT_KEYS = new Set(['ArrowRight', 'ArrowDown']);
const PREV_KEYS = new Set(['ArrowLeft', 'ArrowUp']);

/**
 * Einfachauswahl nach dem ARIA-Radiogruppen-Muster: EIN Tab-Stopp für die
 * ganze Gruppe (die gewählte bzw. erste Option), Pfeiltasten wechseln Fokus
 * und Auswahl, Pos1/Ende springen an den Rand. Sonst müsste man sich per Tab
 * durch jede Kategorie einzeln hangeln.
 */
export function ChoiceGroup({
  id,
  label,
  options,
  value,
  onChange,
  disabled = false,
  invalid = false,
  errorId,
  variant = 'chip',
}: ChoiceGroupProps) {
  const refs = useRef<Array<HTMLButtonElement | null>>([]);
  const selectedIndex = options.findIndex((o) => o.value === value);
  const tabStop = selectedIndex >= 0 ? selectedIndex : 0;

  const select = (index: number) => {
    const option = options[index];
    if (!option) return;
    onChange(option.value);
    refs.current[index]?.focus();
  };

  const handleKeyDown = (event: React.KeyboardEvent, index: number) => {
    const last = options.length - 1;
    let target: number | null = null;
    if (NEXT_KEYS.has(event.key)) target = index === last ? 0 : index + 1;
    else if (PREV_KEYS.has(event.key)) target = index === 0 ? last : index - 1;
    else if (event.key === 'Home') target = 0;
    else if (event.key === 'End') target = last;
    if (target === null) return;
    event.preventDefault();
    select(target);
  };

  return (
    <div
      id={id}
      role="radiogroup"
      aria-label={label}
      aria-invalid={invalid || undefined}
      aria-describedby={invalid && errorId ? errorId : undefined}
      className={cn(variant === 'card' ? 'grid gap-2 sm:grid-cols-2' : 'flex flex-wrap gap-2')}
    >
      {options.map((option, index) => {
        const active = option.value === value;
        const Icon = option.icon;
        return (
          <button
            key={option.value}
            ref={(el) => {
              refs.current[index] = el;
            }}
            type="button"
            role="radio"
            aria-checked={active}
            tabIndex={index === tabStop ? 0 : -1}
            disabled={disabled}
            onClick={() => select(index)}
            onKeyDown={(event) => handleKeyDown(event, index)}
            className={cn(
              'transition-colors disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1967D2] focus-visible:ring-offset-2',
              variant === 'card'
                ? 'flex items-center gap-3 rounded-lg border px-4 py-3 text-left text-sm font-medium'
                : 'rounded-full border px-3 py-1.5 text-sm',
              active
                ? 'border-[#1967D2] bg-[#1967D2]/10 text-[#1967D2]'
                : cn(
                    'hover:bg-muted',
                    variant === 'chip' && 'text-muted-foreground',
                    invalid ? 'border-destructive' : 'border-border',
                  ),
            )}
          >
            {Icon && <Icon className="h-4 w-4" aria-hidden="true" />}
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

export default ChoiceGroup;
